// Our Transportation Network — canonical data for the multi-brand ecosystem.
// Every brand is a specialized website operated by the same parent company
// (Black Chauffeur Limo Service LLC). Each site targets a distinct search
// intent and audience — never duplicate content, never doorway pages.

export type BrandKey =
  | "premier"
  | "blackchauffeur"
  | "siliconvalley"
  | "sanjose"
  | "sfo"
  | "founder";

export interface Brand {
  key: BrandKey;
  name: string;
  url: string;
  host: string;
  tagline: string;
  bestFor: string;
  services: string[];
  purpose: string;
}

export const NETWORK: Brand[] = [
  {
    key: "premier",
    name: "Premier Executive Chauffeur",
    url: "https://premierexecutivechauffeur.com",
    host: "premierexecutivechauffeur.com",
    tagline: "Luxury executive & VIP transportation",
    bestFor:
      "C-suite executives, private-aviation clients, high-end leisure, roadshows, and VIPs across the Bay Area.",
    services: [
      "Executive chauffeur service",
      "Private aviation & FBO transportation",
      "Corporate roadshows",
      "Wine tours & luxury day trips",
    ],
    purpose:
      "Our flagship luxury brand for discerning travelers who expect Manhattan-grade discretion, on-time performance, and late-model black vehicles.",
  },
  {
    key: "blackchauffeur",
    name: "Black Chauffeur Limo Service",
    url: "https://blackchauffeurlimo.com",
    host: "blackchauffeurlimo.com",
    tagline: "Reservations, corporate accounts & customer portal",
    bestFor:
      "Anyone ready to reserve, manage a corporate account, or access the customer portal.",
    services: [
      "Instant online reservations",
      "Corporate account management",
      "Customer portal & invoicing",
      "Company headquarters & dispatch",
    ],
    purpose:
      "The parent company — our primary business operations, dispatch center, and the reservation platform behind every brand in the network.",
  },
  {
    key: "siliconvalley",
    name: "Silicon Valley Chauffeur",
    url: "https://siliconvalleychauffeur.com",
    host: "siliconvalleychauffeur.com",
    tagline: "Silicon Valley corporate mobility",
    bestFor:
      "Executive assistants, tech companies, and business travelers moving between Silicon Valley campuses.",
    services: [
      "Corporate campus transportation",
      "Executive commute programs",
      "Investor & board-meeting logistics",
      "Recurring business travel",
    ],
    purpose:
      "Focused authority on Silicon Valley corporate transportation — Sand Hill Road, Palo Alto, Mountain View, Cupertino, and every tech campus in between.",
  },
  {
    key: "sanjose",
    name: "San Jose Airport Limo",
    url: "https://sanjoseairportlimo.com",
    host: "sanjoseairportlimo.com",
    tagline: "SJC airport transportation specialists",
    bestFor:
      "Travelers arriving at or departing from San José Mineta International (SJC).",
    services: [
      "SJC arrivals & departures",
      "Flight-tracked meet-and-greet",
      "SJC terminal & FBO transfers",
      "Airport travel guides",
    ],
    purpose:
      "The dedicated SJC authority — terminal maps, flight-tracked pickups, and every airport-transfer question answered in one place.",
  },
  {
    key: "sfo",
    name: "SFO Ground Transportation",
    url: "https://sfogroundtransportation.com",
    host: "sfogroundtransportation.com",
    tagline: "San Francisco Airport transportation authority",
    bestFor:
      "Travelers arriving at or departing from San Francisco International (SFO).",
    services: [
      "SFO arrivals & departures",
      "International terminal meet-and-greet",
      "Regional airport transportation",
      "SFO travel & terminal guides",
    ],
    purpose:
      "The dedicated SFO authority — deep guides to every terminal, curbside, and international arrivals hall at San Francisco International.",
  },
  {
    key: "founder",
    name: "Meet Our Founder",
    url: "https://mynameisabdul99.com",
    host: "mynameisabdul99.com",
    tagline: "Founder story, leadership & community",
    bestFor:
      "Anyone interested in the people, values, and journey behind the company.",
    services: [
      "Founder biography",
      "Company history & milestones",
      "Media & press features",
      "Community involvement",
    ],
    purpose:
      "The personal site of our founder — where you'll find the story, the values, and the leadership that shape everything we do.",
  },
];

export const BRANDS: Record<BrandKey, Brand> = Object.fromEntries(
  NETWORK.map((b) => [b.key, b]),
) as Record<BrandKey, Brand>;

// Contextual routing — which sibling brand best matches a given page context.
// Returns a single most-relevant suggestion (or null) so contextual placement
// stays uncluttered and genuinely useful.
export function suggestBrandFor(context: {
  type: "service" | "airport" | "fbo" | "city" | "corporate" | "founder";
  slug?: string;
}): Brand | null {
  const slug = (context.slug ?? "").toLowerCase();

  if (context.type === "founder") return BRANDS.founder;
  if (context.type === "corporate") return BRANDS.blackchauffeur;

  if (context.type === "airport") {
    if (slug === "sfo") return BRANDS.sfo;
    if (slug === "sjc" || slug === "rhv" || slug === "hwd") return BRANDS.sanjose;
    // OAK / MRY / SNS have no dedicated sibling — return null rather than force a mismatch.
    return null;
  }

  if (context.type === "service") {
    if (slug === "airport-transfers") return BRANDS.sfo;
    if (slug === "corporate" || slug === "private-aviation" || slug === "sprinter-groups") {
      return BRANDS.siliconvalley;
    }
    // Wine tours, weddings, sporting events, city tours: no better sibling than us.
    return null;
  }

  if (context.type === "fbo") {
    if (slug.includes("sfo") || slug.includes("signature-sfo") || slug.includes("kabk")) return BRANDS.sfo;
    if (slug.includes("sjc") || slug.includes("ksjc") || slug.includes("atlantic-sjc") || slug.includes("signature-sjc")) {
      return BRANDS.sanjose;
    }
    return null;
  }

  if (context.type === "city") {
    const svRegions = new Set([
      "palo-alto",
      "mountain-view",
      "sunnyvale",
      "cupertino",
      "santa-clara",
      "san-jose",
      "menlo-park",
      "los-altos",
      "los-gatos",
      "saratoga",
      "campbell",
      "milpitas",
    ]);
    if (svRegions.has(slug)) return BRANDS.siliconvalley;
    if (slug === "san-francisco") return BRANDS.sfo;
    return null;
  }

  return null;
}

